import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import HIWHero from './components/HIWHero';
import HIWSteps from './components/HIWSteps';
import HIWPlatforms from './components/HIWPlatforms';
import HIWResults from './components/HIWResults';
import HIWFAQ from './components/HIWFAQ';

export default function HowItWorksPage() {
  return (
    <>
      <title>How AdForge's AI Ad Generator Works | Create Ads with AI in 3 Minutes</title>
      <meta
        name="description"
        content="Learn exactly how to create ads with AI using AdForge. Step-by-step guide: set up your profile, choose your platform, generate AI copy and images, edit, and launch — in under 3 minutes. Free to start."
      />
      <meta
        name="keywords"
        content="how to create ads with AI, how AI ad generator works, AI advertising tutorial, create ads with AI free, AdForge how it works"
      />
      <link rel="canonical" href="https://adforge.ai/how-it-works" />
      <meta name="Last-modified" content={new Date().toISOString().split('T')[0]} />

      <meta property="og:type" content="website" />
      <meta property="og:title" content="How AdForge's AI Ad Generator Works | 3-Minute Guide" />
      <meta property="og:description" content="Step-by-step: how to create professional ads with AI in under 3 minutes. No experience needed. Free to start." />
      <meta property="og:url" content="https://adforge.ai/how-it-works" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content="How to Create Ads with AI | AdForge Step-by-Step" />
      <meta name="twitter:description" content="See exactly how AdForge's AI generates complete ad copy and images in seconds — for any platform." />

      <script type="application/ld+json">{JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'HowTo',
        name: 'How to Create Ads with AI Using AdForge',
        description: 'A step-by-step guide to creating high-converting ads using AdForge\'s AI ad generator in under 3 minutes.',
        url: 'https://adforge.ai/how-it-works',
        totalTime: 'PT3M',
        estimatedCost: { '@type': 'MonetaryAmount', currency: 'USD', value: '0' },
        step: [
          { '@type': 'HowToStep', position: 1, name: 'Set Up Your Business Profile', text: 'Enter your business name, industry, target audience, and key offer.' },
          { '@type': 'HowToStep', position: 2, name: 'Choose Your Platform & Goal', text: 'Select the ad platform (Facebook, Instagram, Google, etc.) and your campaign objective.' },
          { '@type': 'HowToStep', position: 3, name: 'AI Generates Your Complete Ad', text: 'AdForge\'s AI writes the copy and generates a matching creative image in seconds.' },
          { '@type': 'HowToStep', position: 4, name: 'Review and Edit', text: 'Fine-tune your ad in the built-in visual editor.' },
          { '@type': 'HowToStep', position: 5, name: 'Export and Launch', text: 'Download your ad creative and launch directly from your ad manager.' },
        ],
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://adforge.ai' },
            { '@type': 'ListItem', position: 2, name: 'How It Works', item: 'https://adforge.ai/how-it-works' },
          ],
        },
      })}</script>

      <div className="min-h-screen bg-white">
        <Navbar />
        <main>
          <HIWHero />
          <HIWSteps />
          <HIWPlatforms />
          <HIWResults />
          <HIWFAQ />

          {/* Final CTA */}
          <section className="py-20 lg:py-24 relative overflow-hidden">
            <div className="absolute inset-0">
              <img
                src="https://readdy.ai/api/search-image?query=warm%20amber%20golden%20light%20bokeh%20background%20with%20soft%20glowing%20orbs%20on%20a%20very%20dark%20charcoal%20surface%2C%20abstract%20elegant%20minimal%20art%2C%20wide%20panoramic%20format%2C%20sophisticated%20premium%20atmosphere%2C%20no%20text%20or%20shapes&width=1920&height=560&seq=hiw-cta-bg-final-01&orientation=landscape"
                alt=""
                className="w-full h-full object-cover object-top"
                aria-hidden="true"
              />
              <div className="absolute inset-0 bg-black/60" />
            </div>
            <div className="relative z-10 max-w-3xl mx-auto px-6 lg:px-8 text-center">
              <h2 className="text-3xl lg:text-4xl font-black text-white mb-4 leading-tight">
                You Now Know How It Works — Try It for Real
              </h2>
              <p className="text-stone-300 text-base mb-8 leading-relaxed">
                In less time than it took to read this page, you could have your first{' '}
                <strong className="text-white">AI-generated ad</strong> ready to launch. No credit card.
                No experience required.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-6">
                <Link
                  to="/signup"
                  className="px-8 py-4 bg-orange-500 hover:bg-orange-600 text-white font-bold text-base rounded-xl transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Generate My First Ad — Free
                </Link>
                <Link
                  to="/features"
                  className="px-8 py-4 bg-white/10 hover:bg-white/20 border border-white/20 text-white font-semibold text-base rounded-xl transition-colors duration-200 whitespace-nowrap cursor-pointer"
                >
                  Explore All Features
                </Link>
              </div>
              <p className="text-xs text-stone-400">
                Try for free &middot; No credit card &middot; Takes 3 minutes
              </p>
            </div>
          </section>
        </main>
        <Footer />
      </div>
    </>
  );
}
